import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import api from '../utils/api';
import { toast } from 'sonner';
import { Users, Activity, AlertCircle, Trash2, Shield, Crown, CheckCircle, XCircle, RefreshCw, UserCheck, UserX, Zap, ZapOff } from 'lucide-react';

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId, role) => {
    setUpdating(userId);
    try {
      await api.patch(`/admin/users/${userId}`, { role });
      toast.success(`User role updated to ${role}`);
      loadUsers();
    } catch (error) {
      toast.error('Failed to update user role');
    } finally {
      setUpdating(null);
    }
  };

  const updateUserPlan = async (userId, plan) => {
    setUpdating(userId);
    try {
      await api.patch(`/admin/users/${userId}`, { plan });
      toast.success(`User plan updated to ${plan}`);
      loadUsers();
    } catch (error) {
      toast.error('Failed to update user plan');
    } finally {
      setUpdating(null);
    }
  };

  const toggleEmailVerified = async (userId, currentStatus) => {
    setUpdating(userId);
    try {
      await api.patch(`/admin/users/${userId}`, { email_verified: !currentStatus });
      toast.success(`Email verification ${!currentStatus ? 'granted' : 'revoked'}`);
      loadUsers();
    } catch (error) {
      toast.error('Failed to update verification status');
    } finally {
      setUpdating(null);
    }
  };

  const toggleUserActive = async (userId, currentStatus) => {
    setUpdating(userId);
    try {
      await api.patch(`/admin/users/${userId}`, { is_active: !currentStatus });
      toast.success(`User ${!currentStatus ? 'activated - can now build!' : 'deactivated - build access revoked'}`);
      loadUsers();
    } catch (error) {
      toast.error('Failed to update active status');
    } finally {
      setUpdating(null);
    }
  };

  const grantVIP = async (userId) => {
    setUpdating(userId);
    try {
      await api.post(`/admin/grant_vip/${userId}`);
      toast.success('VIP access granted with full build privileges!');
      loadUsers();
    } catch (error) {
      toast.error('Failed to grant VIP access');
    } finally {
      setUpdating(null);
    }
  };

  const revokeVIP = async (userId) => {
    setUpdating(userId);
    try {
      await api.post(`/admin/revoke_vip/${userId}`);
      toast.success('VIP access revoked');
      loadUsers();
    } catch (error) {
      toast.error('Failed to revoke VIP access');
    } finally {
      setUpdating(null);
    }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to delete user ${email}? This will also delete all their projects.`)) {
      return;
    }
    setUpdating(userId);
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setUpdating(null);
    }
  };

  const deleteAllUsers = async () => {
    if (!window.confirm('⚠️ WARNING: This will delete ALL users except your admin account. Are you absolutely sure?')) {
      return;
    }
    if (!window.confirm('This action cannot be undone. Type "DELETE" to confirm... (just click OK)')) {
      return;
    }
    try {
      const response = await api.delete('/admin/users');
      toast.success(`Deleted ${response.data.deleted_count} users`);
      loadUsers();
    } catch (error) {
      toast.error('Failed to delete users');
    }
  };

  const getRoleBadge = (role, isAdmin) => {
    if (isAdmin || role === 'admin') {
      return <Badge className="bg-red-500 text-white"><Shield className="w-3 h-3 mr-1" />Admin</Badge>;
    }
    if (role === 'vip') {
      return <Badge className="bg-yellow-500 text-black"><Crown className="w-3 h-3 mr-1" />VIP</Badge>;
    }
    return <Badge variant="outline" className="capitalize">{role}</Badge>;
  };

  const getPlanBadge = (plan) => {
    const colors = {
      free: 'bg-gray-500',
      pro: 'bg-blue-500',
      business: 'bg-purple-500',
      vip: 'bg-yellow-500 text-black'
    };
    return <Badge className={colors[plan] || 'bg-gray-500'}>{plan}</Badge>;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold tracking-tight text-primary">Admin Dashboard</h1>
            <p className="text-muted-foreground mt-2">Manage users, roles, and system settings</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadUsers} disabled={loading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Link to="/admin/diagnostics">
              <Button data-testid="view-diagnostics-button">
                <Activity className="mr-2 h-4 w-4" />
                Diagnostics
              </Button>
            </Link>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{users.length}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Admins</CardTitle>
              <Shield className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{users.filter(u => u.is_admin || u.role === 'admin').length}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">VIP Members</CardTitle>
              <Crown className="h-4 w-4 text-yellow-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{users.filter(u => u.role === 'vip' || u.plan === 'vip').length}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Can Build</CardTitle>
              <UserCheck className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{users.filter(u => u.is_active || u.is_admin).length}</div>
              <p className="text-xs text-muted-foreground">Users with build access</p>
            </CardContent>
          </Card>
        </div>

        {/* Danger Zone */}
        <Card className="border-red-500/50">
          <CardHeader>
            <CardTitle className="text-red-500 flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Danger Zone
            </CardTitle>
            <CardDescription>Destructive actions that cannot be undone</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="destructive" onClick={deleteAllUsers}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete All Users (Except Admin)
            </Button>
          </CardContent>
        </Card>

        {/* User Management Table */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">User Management</CardTitle>
            <CardDescription>View and manage all registered users</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading users...</div>
            ) : users.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No users found</div>
            ) : (
              <div className="space-y-3">
                {users.map((user) => (
                  <div
                    key={user.user_id}
                    className="flex flex-col md:flex-row md:items-center justify-between p-4 rounded-lg border border-border gap-4"
                    data-testid={`admin-user-${user.user_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium truncate">{user.name}</p>
                        {getRoleBadge(user.role, user.is_admin)}
                        {getPlanBadge(user.plan)}
                        {user.email_verified ? (
                          <Badge variant="outline" className="text-green-500 border-green-500">
                            <CheckCircle className="w-3 h-3 mr-1" />Verified
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-red-500 border-red-500">
                            <XCircle className="w-3 h-3 mr-1" />Unverified
                          </Badge>
                        )}
                        {user.is_active ? (
                          <Badge className="bg-green-600 text-white">
                            <UserCheck className="w-3 h-3 mr-1" />Can Build
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-orange-500 border-orange-500">
                            <UserX className="w-3 h-3 mr-1" />No Access
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                      <p className="text-xs text-muted-foreground">ID: {user.user_id}</p>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Role Selector */}
                      <Select
                        value={user.role}
                        onValueChange={(value) => updateUserRole(user.user_id, value)}
                        disabled={updating === user.user_id}
                      >
                        <SelectTrigger className="w-[120px]">
                          <SelectValue placeholder="Role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="member">Member</SelectItem>
                          <SelectItem value="vip">VIP</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>

                      {/* Plan Selector */}
                      <Select
                        value={user.plan}
                        onValueChange={(value) => updateUserPlan(user.user_id, value)}
                        disabled={updating === user.user_id}
                      >
                        <SelectTrigger className="w-[120px]">
                          <SelectValue placeholder="Plan" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="free">Free</SelectItem>
                          <SelectItem value="pro">Pro</SelectItem>
                          <SelectItem value="business">Business</SelectItem>
                          <SelectItem value="vip">VIP</SelectItem>
                        </SelectContent>
                      </Select>

                      {/* Verify/Unverify Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleEmailVerified(user.user_id, user.email_verified)}
                        disabled={updating === user.user_id}
                        title={user.email_verified ? 'Revoke verification' : 'Grant verification'}
                      >
                        {user.email_verified ? <XCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                      </Button>

                      {/* Activate/Deactivate Build Access */}
                      <Button
                        variant={user.is_active ? "secondary" : "default"}
                        size="sm"
                        onClick={() => toggleUserActive(user.user_id, user.is_active)}
                        disabled={updating === user.user_id || user.is_admin}
                        title={user.is_active ? 'Revoke build access' : 'Grant build access'}
                        className={user.is_active ? '' : 'bg-green-600 hover:bg-green-700'}
                      >
                        {user.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                      </Button>

                      {/* Quick VIP Grant/Revoke */}
                      {user.plan !== 'vip' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => grantVIP(user.user_id)}
                          disabled={updating === user.user_id}
                          title="Grant VIP + Activate"
                          className="border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black"
                        >
                          <Zap className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => revokeVIP(user.user_id)}
                          disabled={updating === user.user_id || user.is_admin}
                          title="Revoke VIP"
                          className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white"
                        >
                          <ZapOff className="h-4 w-4" />
                        </Button>
                      )}

                      {/* Delete Button */}
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => deleteUser(user.user_id, user.email)}
                        disabled={updating === user.user_id || user.is_admin}
                        title={user.is_admin ? 'Cannot delete admin' : 'Delete user'}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;